import { Link, useNavigate } from 'react-router-dom';
import type { BundleReviewReadyItem } from '../lib/bundleReviewContext';

interface Props {
  bundleSlug: string;
  items: BundleReviewReadyItem[];
  currentSlug: string;
  /** False for shoot-kind bundles (BundleScriptView) — there's no approval
   *  concept in a read-only shoot pack, so tabs/options drop the ✓ prefix
   *  and the "approved" tint. Defaults true for review-kind bundles. */
  showTicks?: boolean;
}

/**
 * Sticky chrome mounted above an unchanged <ClientReview/> (review-kind
 * bundles) or the read-only <ClientInterviewView/> (shoot-kind bundles) when
 * a script is opened from inside a bundle (see BundleScriptView). Mirrors
 * the visual language of ClientReview's own .suggest-banner sticky bar; all
 * classes below are semantic-token driven so this restyles dark for free
 * when a shoot bundle's root theme is dark — no separate dark variant needed.
 *
 * Desktop shows a full tab row that scrolls horizontally rather than
 * wrapping, so a bundle with many scripts never breaks the layout. Phones
 * collapse to a native <select> — visually styled as the mockup's
 * "Script N of M ▾" pill, but a real <select> underneath so it stays fully
 * keyboard/screen-reader accessible at any script count.
 */
export default function BundleSwitcher({ bundleSlug, items, currentSlug, showTicks = true }: Props) {
  const navigate = useNavigate();
  const currentIndex = items.findIndex((i) => i.slug === currentSlug);

  return (
    <div
      data-print-hide
      className="sticky top-0 z-30 bg-accent/[0.07] backdrop-blur-md border-b border-accent"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12 min-h-14 py-2 flex flex-wrap items-center gap-3 md:gap-4">
        <Link
          to={`/b/${bundleSlug}`}
          className="flex items-center gap-1.5 font-semibold text-[13px] text-muted hover:text-accent transition-colors shrink-0"
        >
          <span aria-hidden>‹</span> All scripts
        </Link>

        {/* Desktop tab row. */}
        <div className="hidden md:flex items-center gap-1 overflow-x-auto min-w-0 bg-elevated border border-line rounded-full p-1">
          {items.map((item) => {
            const isCurrent = item.slug === currentSlug;
            const isApproved = showTicks && item.status === 'approved';
            return (
              <Link
                key={item.slug}
                to={`/b/${bundleSlug}/${item.slug}`}
                className={`
                  shrink-0 px-3 py-1.5 rounded-full whitespace-nowrap
                  font-mono text-[10px] uppercase tracking-[0.08em] font-semibold
                  transition-colors
                  ${
                    isCurrent
                      ? 'bg-fg text-page'
                      : isApproved
                      ? 'text-accent hover:bg-card'
                      : 'text-muted hover:text-fg hover:bg-card'
                  }
                `}
              >
                {isApproved && !isCurrent ? '✓ ' : ''}
                {item.title}
              </Link>
            );
          })}
        </div>

        {/* Mobile — a decorative "Script N of M ▾" pill with a real, invisible
            <select> layered on top so the whole control stays a native,
            keyboard/screen-reader-friendly dropdown. */}
        <div className="relative md:hidden">
          <div
            aria-hidden
            className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-card border border-line font-mono text-[11px] uppercase tracking-[0.08em] font-semibold text-fg whitespace-nowrap"
          >
            Script {currentIndex + 1} of {items.length}
            <span className="text-faint">▾</span>
          </div>
          <select
            aria-label="Jump to script"
            value={currentSlug}
            onChange={(e) => navigate(`/b/${bundleSlug}/${e.target.value}`)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          >
            {items.map((item, i) => (
              <option key={item.slug} value={item.slug}>
                {showTicks && item.status === 'approved' ? '✓ ' : ''}
                Script {i + 1} — {item.title}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
